import { CreatorOrderV2ContractError } from './errors';
import type { CreatorDeliveryTimeline } from '@/interface/creatorDeliveryTimeline';

const fail = (field: string): never => {
  throw new CreatorOrderV2ContractError(
    `Invalid V2 delivery timeline: ${field}.`
  );
};
const record = (value: any) =>
  value && typeof value === 'object' && !Array.isArray(value);
const dateOrNull = (value: any) =>
  value === null ||
  (typeof value === 'string' && Number.isFinite(Date.parse(value)));
const codes: Record<string, string[]> = {
  CONTENT: [
    'KICKOFF',
    'ADDRESS',
    'ACCOUNT_INFO',
    'SCRIPT',
    'DRAFT',
    'FINAL_LINK'
  ],
  EVENT: [
    'INTEREST_EXPRESSED',
    'DEAL_CONFIRMED',
    'ACCOUNT_READY',
    'CONTENT_APPROVED'
  ],
  EXTENSION: ['TIMELINE_EXTENSION'],
  CANCELLATION: [
    'DEAL_CANCELLED',
    'CREATOR_DECLINED_INVITATION',
    'CREATOR_WITHDREW_APPLICATION',
    'CREATOR_ENDED_NEGOTIATION',
    'CREATOR_ENDED_COLLABORATION',
    'BRAND_ENDED_NEGOTIATION',
    'BRAND_REJECTED_CONTENT',
    'CREATOR_OVERDUE'
  ]
};

/** Validate the boundary without reordering, dropping unknown data or inventing tasks. */
export const assertCreatorDeliveryTimeline = (
  value: any,
  orderId: string
): CreatorDeliveryTimeline => {
  if (
    !record(value) ||
    value.orderId !== orderId ||
    !value.serverNow ||
    !dateOrNull(value.serverNow) ||
    !['ACTIVE', 'COMPLETED', 'CANCELLED'].includes(value.state) ||
    !Array.isArray(value.milestones) ||
    !record(value.focus)
  )
    fail('root metadata');
  const phases = new Set<string>();
  const ids = new Set<string>();
  for (const phase of value.milestones) {
    if (
      !record(phase) ||
      ![
        'NEGOTIATE',
        'START',
        'CREATE',
        'POST',
        'GET_PAID',
        'CANCELLED'
      ].includes(phase.key) ||
      phases.has(phase.key) ||
      !['PENDING', 'CURRENT', 'COMPLETED', 'CANCELLED'].includes(phase.state) ||
      !Array.isArray(phase.nodes)
    )
      fail('milestone');
    phases.add(phase.key);
    if (phase.key === 'GET_PAID' && phase.nodes.length)
      fail('GET_PAID must have empty nodes');
    for (const node of phase.nodes) {
      if (
        !record(node) ||
        typeof node.id !== 'string' ||
        !node.id ||
        ids.has(node.id) ||
        !codes[node.type]?.includes(node.code) ||
        !record(node.data) ||
        !Array.isArray(node.actions) ||
        node.actions.some((action: unknown) => typeof action !== 'string') ||
        ![
          'PENDING',
          'WAITING_CREATOR',
          'WAITING_BRAND',
          'PROCESSING',
          'FEEDBACK_PROVIDED',
          'APPROVED',
          'REJECTED',
          'COMPLETED',
          'CANCELLED'
        ].includes(node.state)
      )
        fail('node metadata');
      ids.add(node.id);
      const d = node.deadline;
      if (
        d !== null &&
        (!record(d) ||
          !dateOrNull(d.dueAt) ||
          !dateOrNull(d.overdueSince) ||
          !dateOrNull(d.overdueUntil) ||
          (d.dueAt && d.relative) ||
          (d.overdueUntil && !d.overdueSince) ||
          (d.relative !== null &&
            (!record(d.relative) ||
              ![
                'DEAL_CONFIRMED',
                'START_COMPLETED',
                'SCRIPT_APPROVED',
                'DRAFT_APPROVED',
                'BRAND_FEEDBACK'
              ].includes(d.relative.after) ||
              !['DAY', 'HOUR'].includes(d.relative.unit) ||
              !Number.isFinite(d.relative.value) ||
              d.relative.value < 0)))
      )
        fail('deadline');
      if (node.type !== 'CONTENT') continue;
      const data = node.data;
      if (data.shipping != null) {
        const shipping = data.shipping;
        if (
          node.code !== 'ADDRESS' ||
          !record(shipping) ||
          !['PENDING', 'WAITING_BRAND', 'PROCESSING', 'COMPLETED'].includes(
            shipping.state
          ) ||
          !dateOrNull(shipping.shippedAt) ||
          !dateOrNull(shipping.arrivedAt) ||
          (shipping.productShipment !== null &&
            !record(shipping.productShipment))
        )
          fail('address shipping');
      }
      if (
        !Number.isInteger(data.version) ||
        data.version < 1 ||
        (data.is_pre_submit !== undefined &&
          typeof data.is_pre_submit !== 'boolean') ||
        !Array.isArray(data.reviews) ||
        (data.contentRoundId !== null &&
          typeof data.contentRoundId !== 'string')
      )
        fail('content metadata');
      const groups =
        data.contentScope === 'COLLABORATION'
          ? [data]
          : data.contentScope === 'CHANNEL' && Array.isArray(data.channels)
            ? data.channels
            : null;
      if (
        !groups ||
        groups.some(
          (group: any) =>
            !Array.isArray(group.submissions) ||
            group.submissions.some(
              (submission: any) =>
                !record(submission.content) ||
                typeof submission.content.url !== 'string'
            )
        )
      )
        fail('submissions');
      if (data.target !== null) {
        const target = data.target;
        if (
          !record(target) ||
          target.stage !== data.stage ||
          target.version !== data.version ||
          (target.expectedSubmissionRevision !== undefined &&
            (!Number.isSafeInteger(target.expectedSubmissionRevision) ||
              target.expectedSubmissionRevision < 0)) ||
          !Array.isArray(target.channels) ||
          target.channels.length === 0 ||
          new Set(target.channels.map((channel: any) => channel.channelKey))
            .size !== target.channels.length ||
          target.channels.some(
            (channel: any) =>
              !channel.channelKey ||
              !Number.isSafeInteger(channel.expectedDeliveryRevision) ||
              channel.expectedDeliveryRevision < 0
          )
        )
          fail('target');
      }
    }
  }
  const focus = value.milestones.find(
    (phase: any) => phase.key === value.focus.milestoneKey
  );
  if (
    !focus ||
    (value.focus.nodeId !== null &&
      !focus.nodes.some((node: any) => node.id === value.focus.nodeId))
  )
    fail('focus');
  return value;
};
